import type { EntityName } from '@provision/entity-types';
import { getBulkUpdateRegistry } from './registry.js';
import type { BulkUpdateAttributeDefinition } from './types.js';

export { getBulkUpdateRegistry } from './registry.js';
export * from './constants.js';
export * from './metadata.js';
export * from './validation.js';
export type {
  BulkUpdateAttributeDefinition,
  BulkUpdateConfirmationDefinition,
} from './types.js';

export function getAttributeConfig(
  entityName: EntityName,
  attribute: string,
): BulkUpdateAttributeDefinition | undefined {
  return ensureByKey().get(registryKey(entityName, attribute));
}

export function getAttributeConfigOrThrow(
  entityName: EntityName,
  attribute: string,
): BulkUpdateAttributeDefinition {
  const config = getAttributeConfig(entityName, attribute);
  if (!config) {
    throw new Error(
      `Attribute "${attribute}" is not registered for bulk update on entity "${entityName}"`,
    );
  }
  return config;
}

export function getEditableAttributes(
  entityName: EntityName,
): BulkUpdateAttributeDefinition[] {
  return ensureByEntity().get(entityName) ?? [];
}

export function isAttributeBulkEditable(entityName: EntityName, attribute: string): boolean {
  return ensureByKey().has(registryKey(entityName, attribute));
}

// below are internal utils not exposed anywhere
type RegistryIndex = Map<string, BulkUpdateAttributeDefinition>;

type EntityIndex = Map<EntityName, BulkUpdateAttributeDefinition[]>;

let _byKey: RegistryIndex | null = null;
let _byEntity: EntityIndex | null = null;

function registryKey(entityName: string, attribute: string): string {
  return `${entityName}:${attribute}`;
}

function ensureByKey(): RegistryIndex {
  if (!_byKey) {
    _byKey = new Map(
      getBulkUpdateRegistry().map((def) => [registryKey(def.entityName, def.attribute), def]),
    );
  }
  return _byKey;
}

function ensureByEntity(): EntityIndex {
  if (!_byEntity) {
    _byEntity = new Map<EntityName, BulkUpdateAttributeDefinition[]>();
    for (const def of getBulkUpdateRegistry()) {
      const list = _byEntity.get(def.entityName) ?? [];
      list.push(def);
      _byEntity.set(def.entityName, list);
    }
  }
  return _byEntity;
}
